/**
 * Animation du bonhomme: du code, pas de skinning.
 *
 * Chaque action calcule une pose (des angles d'articulations) a partir d'une
 * horloge, et on interpole vers cette pose sur un court fondu pour eviter les
 * sauts. La cadence du pas est calee sur la vitesse reelle du deplacement,
 * donc les pieds ne patinent pas.
 *
 * Les coudes sont des articulations a part entiere: un bras qui se plie vaut
 * mieux qu'un bras-baton qu'on fait tourner depuis l'epaule. C'est ce qui rend
 * le balancement de la marche, le geste de manger et le salut credibles.
 *
 * `phasePas()` et `piedAuSol()` sont l'interface du module audio: la phase
 * passe par 0 et 0.5 exactement quand un pied touche le sol.
 */
import { RUN_SPEED, WALK_SPEED } from '../../shared/constants.ts'
import type { EtatJoueur } from '../../shared/types/player.ts'
import { clamp, lerp, creerAlea } from '../../shared/sim/alea.ts'
import type { Bonhomme } from './bonhomme.ts'

export type ActionBonhomme =
  | 'immobile'
  | 'marche'
  | 'course'
  | 'recolte'
  | 'mange'
  | 'dort'
  | 'construit'
  | 'salue'
  | 'nage'

/** L'etat du joueur est toujours une action valide: garantie a la compilation. */
export function actionDepuisEtat(etat: EtatJoueur): ActionBonhomme {
  return etat
}

const TAU = Math.PI * 2

/** Duree du fondu entre deux actions, en secondes. */
const FONDU = 0.15

/**
 * Longueur d'un pas, en metres. Fixe la cadence a partir de la vitesse.
 * Choisie pour la cadence a l'oeil (environ 4 pas/s a WALK_SPEED, 5 a
 * RUN_SPEED): sans genou, l'amplitude de jambe qui donnerait exactement cette
 * enjambee ferait tanguer le bassin de 20 cm.
 */
const PAS_MARCHE = 0.95
const PAS_COURSE = 1.35

/** Amplitude de la jambe, en radians. Elle commande directement le tangage. */
const AMP_JAMBE_MARCHE = 0.44
const AMP_JAMBE_COURSE = 0.55

/** Part de la rotation de jambe annulee par la cheville. 1 = semelle toujours a plat. */
const PLAT_PIED = 0.9

/** Vitesse minimale utilisee pour la cadence: evite le gel si on pousse un mur. */
const VITESSE_PLANCHER = 0.7

/** Flexion de coude au repos: un bras parfaitement tendu fait mannequin. */
const COUDE_REPOS = -0.17

interface Pose {
  bassinY: number
  bassinRotX: number
  bassinRotZ: number
  torseRotX: number
  torseRotY: number
  torseRotZ: number
  teteRotX: number
  teteRotY: number
  teteRotZ: number
  brasGX: number
  brasGZ: number
  brasDX: number
  brasDZ: number
  /** Flexion des coudes. Negatif = l'avant-bras se replie vers l'avant. */
  coudeGX: number
  coudeDX: number
  mainGX: number
  mainDX: number
  jambeGX: number
  jambeGZ: number
  jambeDX: number
  jambeDZ: number
  /** Garde au sol demandee sous chaque pied, en metres. */
  leveG: number
  leveD: number
  /**
   * 1 = les pieds sont ancres au sol et la longueur des jambes est calculee
   * pour cela. 0 = jambes a leur longueur naturelle, pour les poses ou le
   * bonhomme ne se tient pas debout.
   */
  ancrage: number
}

function poseNeutre(): Pose {
  return {
    bassinY: 0,
    bassinRotX: 0,
    bassinRotZ: 0,
    torseRotX: 0,
    torseRotY: 0,
    torseRotZ: 0,
    teteRotX: 0,
    teteRotY: 0,
    teteRotZ: 0,
    brasGX: 0,
    brasGZ: -0.09,
    brasDX: 0,
    brasDZ: 0.09,
    coudeGX: COUDE_REPOS,
    coudeDX: COUDE_REPOS,
    mainGX: 0,
    mainDX: 0,
    jambeGX: 0,
    jambeGZ: 0,
    jambeDX: 0,
    jambeDZ: 0,
    leveG: 0,
    leveD: 0,
    ancrage: 1,
  }
}

const CLES = Object.keys(poseNeutre()) as (keyof Pose)[]

/** Pose de reference. La cible repart de la a chaque frame: aucun canal ne traine. */
const NEUTRE = poseNeutre()

/** Compteur de graines: chaque bonhomme cligne des yeux a son rythme. */
let graineSuivante = 0x9e3779b9

export class AnimationBonhomme {
  private readonly bonhomme: Bonhomme
  private courante: ActionBonhomme = 'immobile'

  /** Pose calculee par l'action, pose de depart du fondu, pose appliquee. */
  private readonly cible = poseNeutre()
  private readonly depart = poseNeutre()
  private readonly sortie = poseNeutre()

  /** Horloge continue: les mouvements de repos ne repartent jamais de zero. */
  private horloge = 0
  /** Temps passe dans l'action courante. */
  private temps = 0
  private fondu = FONDU
  private phaseInterne = 0
  private contact = false

  private readonly alea: () => number
  private ouvertureYeux = 1
  private prochainClignement = 3
  private tempsClignement = -1
  /** Decalage de phase propre a chaque bonhomme: ils ne respirent pas ensemble. */
  private readonly dephase: number

  constructor(bonhomme: Bonhomme) {
    this.bonhomme = bonhomme
    graineSuivante = (graineSuivante + 0x85ebca6b) | 0
    this.alea = creerAlea(graineSuivante)
    this.prochainClignement = 1.5 + this.alea() * 4
    this.dephase = this.alea() * 10
    this.horloge = this.dephase
    this.poseImmobile(0)
    for (const k of CLES) {
      this.depart[k] = this.cible[k]
      this.sortie[k] = this.cible[k]
    }
    this.appliquer()
  }

  get action(): ActionBonhomme {
    return this.courante
  }

  /** Change d'animation avec un fondu court. Rejouer la meme action ne fait rien. */
  jouer(action: ActionBonhomme): void {
    if (action === this.courante) return
    for (const k of CLES) this.depart[k] = this.sortie[k]
    this.courante = action
    this.temps = 0
    this.fondu = 0
    // La phase du pas survit au passage marche <-> course: pas de saut de jambe.
    const locomotion = action === 'marche' || action === 'course'
    if (!locomotion) this.phaseInterne = 0
  }

  /**
   * `vitesse` en m/s: la cadence du pas en decoule, donc les pieds collent au
   * sol quelle que soit la vitesse reelle (pente, ralentissement par la faim).
   */
  maj(dt: number, vitesse: number): void {
    const d = Math.min(dt, 0.1)
    this.horloge += d
    this.temps += d
    this.fondu = Math.min(FONDU, this.fondu + d)
    this.contact = false
    for (const k of CLES) this.cible[k] = NEUTRE[k]

    const a = this.courante
    if (a === 'marche' || a === 'course') this.poseLocomotion(d, vitesse, a === 'course')
    else if (a === 'recolte') this.poseRecolte()
    else if (a === 'mange') this.poseMange()
    else if (a === 'dort') this.poseDort()
    else if (a === 'construit') this.poseConstruit()
    else if (a === 'salue') this.poseSalue()
    else if (a === 'nage') this.poseNage(d, vitesse)
    else this.poseImmobile(vitesse)

    const t = this.fondu >= FONDU ? 1 : this.fondu / FONDU
    const s = t * t * (3 - 2 * t)
    for (const k of CLES) this.sortie[k] = lerp(this.depart[k], this.cible[k], s)

    this.appliquer()
    this.majYeux(d)
  }

  /** 0..1. Un pied touche le sol a 0 et a 0.5. */
  phasePas(): number {
    return this.phaseInterne
  }

  /** Vrai sur la seule frame ou un pied vient de toucher le sol. */
  piedAuSol(): boolean {
    return this.contact
  }

  // ------------------------------------------------------------------ poses

  private poseImmobile(vitesse: number): void {
    const c = this.cible
    const resp = Math.sin((TAU * this.horloge) / 4.6)
    const sway = Math.sin((TAU * this.horloge) / 6.9)
    // Report du poids d'une jambe sur l'autre, tres lentement.
    const appui = Math.sin((TAU * this.horloge) / 9.4)
    // Un reste de vitesse fait finir le mouvement en douceur.
    const reste = clamp(vitesse / WALK_SPEED, 0, 1)

    // Toujours positif: le bassin ne descend jamais sous sa hauteur de repos,
    // sinon les pieds s'enfoncent dans le sol.
    c.bassinY = 0.004 * (1 + resp)
    c.bassinRotX = 0
    c.bassinRotZ = 0.012 * sway + 0.03 * appui
    c.torseRotX = -0.03 + 0.024 * resp
    c.torseRotY = 0.03 * sway
    c.torseRotZ = -0.015 * sway - 0.022 * appui
    c.teteRotX = 0.02 - 0.02 * resp
    // Le regard flane: deux periodes incommensurables, donc jamais de boucle.
    c.teteRotY = 0.1 * Math.sin((TAU * this.horloge) / 11.3) + 0.05 * Math.sin((TAU * this.horloge) / 4.1)
    c.teteRotZ = 0.02 * sway + 0.012 * appui
    const ecart = 0.1 + 0.02 * resp
    c.brasGX = -0.04 * resp - 0.25 * reste
    c.brasDX = -0.04 * resp + 0.25 * reste
    c.brasGZ = -ecart
    c.brasDZ = ecart
    // La respiration remonte jusque dans les coudes: rien n'est jamais fige.
    c.coudeGX = COUDE_REPOS - 0.05 - 0.03 * resp + 0.04 * appui
    c.coudeDX = COUDE_REPOS - 0.05 - 0.03 * resp - 0.04 * appui
    c.mainGX = 0.12
    c.mainDX = 0.12
    c.jambeGX = 0.008 * appui
    c.jambeDX = -0.008 * appui
    c.jambeGZ = -0.02 - 0.018 * Math.max(0, appui)
    c.jambeDZ = 0.02 + 0.018 * Math.max(0, -appui)
  }

  private poseLocomotion(dt: number, vitesse: number, course: boolean): void {
    const c = this.cible
    const v = Math.max(vitesse, VITESSE_PLANCHER)
    const longueurPas = course ? PAS_COURSE : PAS_MARCHE

    // Un cycle complet = deux pas.
    const avant = this.phaseInterne
    const brut = avant + (v * dt) / (2 * longueurPas)
    if (brut >= 1 || (avant < 0.5 && brut >= 0.5)) this.contact = true
    this.phaseInterne = brut - Math.floor(brut)

    const p = this.phaseInterne
    const cos = Math.cos(TAU * p)
    const sin = Math.sin(TAU * p)
    // 1 a la vitesse nominale de l'allure: l'amplitude suit la vitesse reelle.
    const f = clamp(v / (course ? RUN_SPEED : WALK_SPEED), 0.35, 1.15)
    const ampJambe = (course ? AMP_JAMBE_COURSE : AMP_JAMBE_MARCHE) * f
    const ampBras = (course ? 0.8 : 0.4) * f

    // Cloche de vol de chaque jambe: nulle pendant l'appui, maximale a mi-vol.
    // La jambe gauche est en appui de 0 a 0.5, la droite de 0.5 a 1.
    const volG = Math.max(0, -sin)
    const volD = Math.max(0, sin)
    const allonge = (course ? 0.2 : 0.12) * f
    const garde = (course ? 0.1 : 0.055) * f

    c.jambeGX = -ampJambe * cos - allonge * volG
    c.jambeDX = ampJambe * cos - allonge * volD
    c.jambeGZ = -0.03
    c.jambeDZ = 0.03
    c.leveG = garde * volG
    c.leveD = garde * volD
    c.brasGX = ampBras * cos + (course ? -0.32 : -0.05)
    c.brasDX = -ampBras * cos + (course ? -0.32 : -0.05)
    c.brasGZ = -(course ? 0.16 : 0.11)
    c.brasDZ = course ? 0.16 : 0.11
    // Le coude se plie surtout quand le bras part vers l'avant: c'est ce
    // detail qui distingue une marche d'un pendule.
    const avantG = Math.max(0, -cos)
    const avantD = Math.max(0, cos)
    const base = course ? -0.95 : -0.22
    const gain = course ? -0.55 : -0.6
    c.coudeGX = base + gain * avantG * f
    c.coudeDX = base + gain * avantD * f
    c.mainGX = course ? -0.45 : -0.12
    c.mainDX = course ? -0.45 : -0.12

    // Le bassin descend exactement de ce que perd la jambe d'appui en pivotant:
    // elle reste tendue, le pied ne quitte jamais le sol, et le creux a chaque
    // contact donne tout son poids au pas.
    const appui = ampJambe * Math.abs(cos)
    c.bassinY = -this.bonhomme.longueurJambe * (1 - Math.cos(appui))
    c.bassinRotX = 0
    // Bascule laterale du bassin du cote de la jambe qui balance.
    c.bassinRotZ = 0.055 * f * cos

    const lean = clamp(vitesse / RUN_SPEED, 0, 1)
    c.torseRotX = -(course ? 0.26 : 0.1) - 0.12 * lean
    c.torseRotY = -0.12 * f * cos
    c.torseRotZ = 0.035 * f * sin - 0.03 * f * cos
    // La tete annule la rotation du torse: le regard reste sur la route.
    c.teteRotX = (course ? 0.1 : 0.03) + 0.03 * f * sin
    c.teteRotY = -c.torseRotY * 0.8
    c.teteRotZ = -0.02 * f * sin
  }

  private poseRecolte(): void {
    const c = this.cible
    const u = (this.temps / 0.85) % 1
    // g = 1 bras leves, g = 0 fin de la frappe. Montee lente, frappe seche.
    const g = u < 0.55 ? this.lisse(u / 0.55) : 1 - this.lisse((u - 0.55) / 0.45) ** 0.6

    c.bassinY = 0.008 + 0.01 * g
    c.bassinRotX = 0
    c.bassinRotZ = 0
    c.torseRotX = lerp(-0.38, 0.1, g)
    c.torseRotY = -0.14
    c.torseRotZ = 0
    c.teteRotX = lerp(-0.24, 0.06, g)
    c.teteRotY = 0.1
    c.teteRotZ = 0
    // Armer: l'outil part derriere l'epaule, coudes tres plies. Frapper: tout
    // se detend d'un coup, epaule et coude en meme temps.
    const bras = -(0.4 + 1.75 * g)
    c.brasGX = bras
    c.brasDX = bras
    c.brasGZ = 0.16
    c.brasDZ = -0.16
    const coude = lerp(-0.35, -1.55, g)
    c.coudeGX = coude
    c.coudeDX = coude
    c.mainGX = lerp(-0.15, -0.5, g)
    c.mainDX = lerp(-0.15, -0.5, g)
    c.jambeGX = -0.16
    c.jambeDX = 0.12
    c.jambeGZ = -0.06
    c.jambeDZ = 0.06
  }

  private poseMange(): void {
    const c = this.cible
    const b = Math.sin((TAU * this.temps) / 1.5)
    const hoche = Math.sin((TAU * this.temps) / 0.75)

    c.bassinY = 0.003 * (1 + b)
    c.bassinRotX = 0
    c.bassinRotZ = 0
    c.torseRotX = -0.06
    c.torseRotY = -0.12
    c.torseRotZ = 0
    c.teteRotX = -0.16 + 0.07 * hoche
    c.teteRotY = 0.05
    c.teteRotZ = 0
    c.brasGX = -0.1
    c.brasGZ = -0.12
    c.coudeGX = -0.35
    // La main droite monte a la bouche: c'est le COUDE qui la porte, l'epaule
    // reste basse. Avant, le bras tendu passait au-dessus de la tete.
    c.brasDX = -0.42 + 0.06 * b
    c.brasDZ = -0.34
    c.coudeDX = -2.05 - 0.16 * b
    c.mainGX = 0.12
    c.mainDX = -0.35
    c.jambeGX = 0
    c.jambeDX = 0
    c.jambeGZ = -0.03
    c.jambeDZ = 0.03
  }

  private poseDort(): void {
    const c = this.cible
    const resp = Math.sin((TAU * this.temps) / 3.4)

    // Le bassin tourne: tout le corps se couche, tete vers -z. Plus de pieds au
    // sol, donc plus d'ancrage: les jambes gardent leur longueur.
    c.ancrage = 0
    c.bassinRotX = -Math.PI / 2
    c.bassinY = -(this.bonhomme.hauteurBassin - 0.225) + 0.012 * resp
    c.bassinRotZ = 0
    // Le torse et la tete sont presque droits: un angle ici, multiplie par la
    // longueur du corps couche, ferait asseoir le bonhomme.
    c.torseRotX = 0.03 + 0.04 * resp
    c.torseRotY = 0.06
    c.torseRotZ = 0
    c.teteRotX = 0.07
    c.teteRotY = 0.12
    c.teteRotZ = 0.26
    // Bras replies sur le ventre, comme on dort vraiment.
    c.brasGX = 0.24
    c.brasDX = 0.18
    c.brasGZ = -0.5
    c.brasDZ = 0.54
    c.coudeGX = -1.15 + 0.05 * resp
    c.coudeDX = -1.3 - 0.05 * resp
    c.mainGX = -0.2
    c.mainDX = -0.2
    c.jambeGX = 0.17
    c.jambeDX = 0.1
    c.jambeGZ = -0.12
    c.jambeDZ = 0.1
  }

  private poseConstruit(): void {
    const c = this.cible
    const u = Math.sin((TAU * this.temps) / 1.1)

    c.bassinY = 0.007 + 0.004 * u
    c.bassinRotX = 0
    c.bassinRotZ = 0
    c.torseRotX = -0.2
    c.torseRotY = 0
    c.torseRotZ = 0
    c.teteRotX = -0.3
    c.teteRotY = 0
    c.teteRotZ = 0
    // Les deux mains travaillent devant soi: epaules basses, coudes plies, et
    // c'est le coude qui donne le va-et-vient.
    c.brasGX = -0.5 + 0.1 * u
    c.brasDX = -0.5 - 0.1 * u
    c.brasGZ = 0.24
    c.brasDZ = -0.24
    c.coudeGX = -1.5 + 0.42 * u
    c.coudeDX = -1.5 - 0.42 * u
    c.mainGX = -0.35 - 0.25 * u
    c.mainDX = -0.35 + 0.25 * u
    c.jambeGX = -0.1
    c.jambeDX = 0.08
    c.jambeGZ = -0.07
    c.jambeDZ = 0.07
  }

  /**
   * Brasse.
   *
   * Avant, nager reutilisait le cycle de marche: le bonhomme traversait la mer
   * en marchant dans le vide, couche sur le ventre. La brasse est le bon geste
   * ici parce qu'elle est SYMETRIQUE — les deux bras font la meme chose, les
   * deux jambes aussi — donc lisible de tres loin et en vue de trois quarts,
   * la ou un crawl ne montrerait qu'un bras qui disparait sous l'eau.
   *
   * Un cycle = quatre temps, et la propulsion ne vient pas de partout: on avance
   * pendant la poussee (bras qui s'ouvrent, jambes qui se detendent) et on se
   * laisse glisser pendant la coulee, bras tendus devant. C'est cette pause qui
   * fait lire le geste comme une nage plutot que comme une gesticulation.
   */
  private poseNage(dt: number, vitesse: number): void {
    const c = this.cible
    // La cadence suit la vitesse, mais mollement: on nage lentement meme quand
    // on avance a peine, sinon le bonhomme s'agite sur place.
    const v = Math.max(vitesse, VITESSE_PLANCHER)
    this.phaseInterne = (this.phaseInterne + dt * (0.55 + 0.09 * v)) % 1
    const p = this.phaseInterne

    // Les deux temps du cycle. `pousse` monte pendant la traction, `coulee`
    // pendant le glissement bras tendus.
    const pousse = Math.sin(TAU * p)
    const ouvert = (1 - Math.cos(TAU * p)) / 2

    // BRAS. Toujours devant (rotation X franchement negative: on tend vers
    // l'avant), ils s'ecartent sur la traction puis se rejoignent.
    const tendu = -1.85 + 0.55 * ouvert
    c.brasGX = tendu
    c.brasDX = tendu
    c.brasGZ = -0.12 - 0.62 * ouvert
    c.brasDZ = 0.12 + 0.62 * ouvert
    // Le coude se plie au moment ou les mains reviennent sous la poitrine.
    c.coudeGX = -0.15 - 1.15 * Math.max(0, -pousse)
    c.coudeDX = c.coudeGX
    c.mainGX = -0.25
    c.mainDX = -0.25

    // JAMBES. Le fouet de brasse est en retard d'un quart de temps sur les
    // bras: on ramene les talons pendant que les bras reviennent, on detend
    // quand ils repartent devant. Sans genou, le repli se lit par l'ecart.
    const q = (p + 0.25) % 1
    const repli = (1 - Math.cos(TAU * q)) / 2
    c.jambeGX = -0.12 - 0.5 * repli
    c.jambeDX = -0.12 - 0.5 * repli
    c.jambeGZ = -0.06 - 0.42 * repli
    c.jambeDZ = 0.06 + 0.42 * repli
    // Les jambes ne portent rien: on coupe l'ancrage au sol, sinon elles se
    // raccourcissent pour aller chercher un plancher qui n'existe pas.
    c.ancrage = 0
    c.leveG = 0
    c.leveD = 0

    // CORPS. Le bassin monte et descend au rythme de la poussee, la tete se
    // leve pour respirer juste avant que les bras ne repartent devant.
    c.bassinY = 0.035 * pousse
    c.bassinRotX = -0.12 - 0.1 * pousse
    c.torseRotX = 0.16 + 0.1 * Math.max(0, pousse)
    c.teteRotX = 0.3 + 0.35 * Math.max(0, -pousse)
  }

  private poseSalue(): void {
    const c = this.cible
    const agite = Math.sin(TAU * this.temps * 1.9)

    c.bassinY = 0.005 * (1 + Math.sin((TAU * this.temps) / 2.2))
    c.bassinRotX = 0
    c.bassinRotZ = 0.02
    c.torseRotX = -0.03
    c.torseRotY = -0.12
    c.torseRotZ = -0.03
    c.teteRotX = 0.05
    c.teteRotY = -0.06
    c.teteRotZ = 0.07
    c.brasGX = -0.05
    c.brasGZ = -0.12
    c.coudeGX = COUDE_REPOS - 0.1
    // Bras leve sur le cote, coude plie, et c'est l'avant-bras qui fait signe.
    c.brasDX = -0.12
    c.brasDZ = 1.85
    c.coudeDX = -0.35 + 0.55 * agite
    c.mainGX = 0.12
    c.mainDX = 0.25 * agite
    c.jambeGX = 0
    c.jambeDX = 0
    c.jambeGZ = -0.03
    c.jambeDZ = 0.03
  }

  private lisse(x: number): number {
    const t = clamp(x, 0, 1)
    return t * t * (3 - 2 * t)
  }

  // --------------------------------------------------------------- rendu pose

  private appliquer(): void {
    const a = this.bonhomme.articulations
    const p = this.sortie

    a.bassin.position.y = this.bonhomme.hauteurBassin + p.bassinY
    a.bassin.rotation.x = p.bassinRotX
    a.bassin.rotation.z = p.bassinRotZ
    a.torse.rotation.set(p.torseRotX, p.torseRotY, p.torseRotZ)
    a.tete.rotation.set(p.teteRotX, p.teteRotY, p.teteRotZ)
    a.brasG.rotation.set(p.brasGX, 0, p.brasGZ)
    a.brasD.rotation.set(p.brasDX, 0, p.brasDZ)
    a.coudeG.rotation.x = p.coudeGX
    a.coudeD.rotation.x = p.coudeDX
    a.mainG.rotation.x = p.mainGX
    a.mainD.rotation.x = p.mainDX
    a.jambeG.rotation.set(p.jambeGX, 0, p.jambeGZ)
    a.jambeD.rotation.set(p.jambeDX, 0, p.jambeDZ)
    // La cheville annule presque la rotation de la jambe: la semelle reste a
    // plat sur le sol au lieu de piquer dedans, et le reste donne le deroule
    // du pied. C'est ce detail qui enleve le flottement.
    a.piedG.rotation.x = -p.jambeGX * PLAT_PIED
    a.piedD.rotation.x = -p.jambeDX * PLAT_PIED

    // Longueur de chaque jambe: celle qui porte reste tendue (facteur 1, le
    // pied pile au sol), celle qui vole se raccourcit juste ce qu'il faut pour
    // obtenir la garde au sol demandee.
    const hanche = this.bonhomme.hauteurBassin + p.bassinY - this.bonhomme.hauteurCheville
    const L = this.bonhomme.longueurJambe
    const jambe = (angle: number, leve: number): number =>
      lerp(1, clamp((hanche - leve) / (L * Math.cos(angle)), 0.7, 1), p.ancrage)
    this.bonhomme.reglerJambe(true, jambe(p.jambeGX, p.leveG))
    this.bonhomme.reglerJambe(false, jambe(p.jambeDX, p.leveD))
  }

  /** Clignement: hors du fondu de pose, sinon il serait etale et invisible. */
  private majYeux(dt: number): void {
    const yeux = this.bonhomme.yeuxMailles
    if (yeux.length === 0) return

    if (this.courante === 'dort') {
      this.ouvertureYeux = Math.max(0.06, this.ouvertureYeux - dt * 6)
      this.tempsClignement = -1
    } else if (this.tempsClignement >= 0) {
      this.tempsClignement += dt
      const u = this.tempsClignement / 0.14
      this.ouvertureYeux = u >= 1 ? 1 : Math.max(0.06, Math.abs(u * 2 - 1))
      if (u >= 1) {
        this.tempsClignement = -1
        this.prochainClignement = 2 + this.alea() * 4.5
      }
    } else {
      this.ouvertureYeux = Math.min(1, this.ouvertureYeux + dt * 8)
      this.prochainClignement -= dt
      if (this.prochainClignement <= 0) this.tempsClignement = 0
    }

    for (const oeil of yeux) oeil.scale.y = this.ouvertureYeux
  }
}
